import { Combobox, Transition } from "@headlessui/react";
import { useState, Fragment, useEffect, Dispatch, SetStateAction } from "react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

function classNames(...classes: string[]) {
	return classes.filter(Boolean).join(" ");
}

export default function Searchbox({
	name,
	options,
	filters,
	setFilters,
}: {
	name: string;
	options: string[];
	filters: string[];
	setFilters: Dispatch<SetStateAction<string[]>>;
}) {
	const [query, setQuery] = useState("");
	const [filteredOptions, setFilteredOptions] = useState(options);

	useEffect(() => {
		if (query === "") return setFilteredOptions(options);
		const filtered = options.filter((option) => option.toLowerCase().includes(query.toLowerCase()));
		return setFilteredOptions(filtered.length === 0 ? options : filtered);
	}, [query, options]);

	useEffect(() => {
		setQuery("");
	}, [filters]);
	return (
		<fieldset>
			<legend className="block font-medium">
				{name}
				{filters.length !== 0 && (
					<>
						{" "}
						({filters.length}) —{" "}
						<button
							className="font-bold text-cary-400 hover:underline"
							onClick={() => {
								setFilters([]);
							}}>
							Reset
						</button>
					</>
				)}
			</legend>
			<div className="space-y-6 pt-6 sm:space-y-4 sm:pt-4">
				<Combobox value={filters} onChange={setFilters} multiple>
					{({ open }) => (
						<>
							<div className="relative max-w-xs">
								<Combobox.Button as="div">
									<Combobox.Input
										className="relative w-full max-w-xs cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm placeholder:text-gray-400 focus:border-cary-400 focus:outline-none focus:ring-1 focus:ring-cary-400 sm:text-sm"
										displayValue={(selected: string[]) => selected.join(", ")}
										onChange={(event) => setQuery(event.target.value)}
										placeholder="Filter by..."
									/>
									<Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
									</Combobox.Button>
								</Combobox.Button>

								<Transition
									show={open}
									as={Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0">
									<Combobox.Options className="absolute z-10 mt-1 max-h-56 w-full max-w-xs overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
										{filteredOptions.map((title) => (
											<Combobox.Option
												key={title}
												className={({ active }) =>
													classNames(
														active ? "bg-cary-400 text-white" : "text-gray-900",
														"relative cursor-default select-none py-2 pl-3 pr-9"
													)
												}
												value={title}>
												{({ selected, active }) => (
													<>
														<div className="flex">
															<span className={classNames(selected ? "font-semibold" : "font-normal", "truncate")}>
																{title}
															</span>
														</div>

														{selected ? (
															<span
																className={classNames(
																	active ? "text-white" : "text-cary-400",
																	"absolute inset-y-0 right-0 flex items-center pr-4"
																)}>
																<CheckIcon className="h-5 w-5" aria-hidden="true" />
															</span>
														) : null}
													</>
												)}
											</Combobox.Option>
										))}
									</Combobox.Options>
								</Transition>
							</div>
						</>
					)}
				</Combobox>
			</div>
		</fieldset>
	);
}
